import { CalendarIcon, HistoryIcon, ListTodoIcon, MailIcon, MilestoneIcon, PhoneIcon, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DataTable, EmptyState, Panel, Pill, useFromHere } from '@bcecloud/portal-ui';
import { formatMoney } from '@/lib/cash-flow';
import { eventStageTone } from '@/lib/event-status';
import { sortRows } from '@/lib/facet-table';
import { salesContactPath, salesAccountPath, TIMELINE_KIND, type ContactRow, type DealRow, type TimelineItem, type TimelineKind } from '@/lib/sales-data';
import { formatDay } from '@/lib/sales-workspace';
import { useTableSort } from '@/lib/use-table-sort';
import { DealLink } from './DealLink';

/**
 * The pieces the sales account, contact and opportunity pages share: links
 * that remember where they were opened from, the engagement timeline, deal
 * and contact tables, and the do-not-contact flags.
 */

const money = (value: number | null): string => (value === null ? '—' : formatMoney(value));

/** A link to an account or contact page whose back arrow returns here. */
export function RecordLink({ to, className, children }: { readonly to: string; readonly className?: string; readonly children: React.ReactNode }): React.ReactElement {
  return (
    <Link to={to} state={useFromHere()} className={className ?? 'text-brand hover:underline'}>
      {children}
    </Link>
  );
}

export function AccountLink({ id, name, className }: { readonly id: string; readonly name: string; readonly className?: string }): React.ReactElement {
  if (!id) return <span>{name || '—'}</span>;
  return (
    <RecordLink to={salesAccountPath(id)} {...(className ? { className } : {})}>
      {name || 'Untitled account'}
    </RecordLink>
  );
}

export function ContactLink({ id, name, className }: { readonly id: string; readonly name: string; readonly className?: string }): React.ReactElement {
  if (!id) return <span>{name || '—'}</span>;
  return (
    <RecordLink to={salesContactPath(id)} {...(className ? { className } : {})}>
      {name || 'Unnamed contact'}
    </RecordLink>
  );
}

// ─── Do-not-contact flags ───────────────────────────────────────────────────

/** "No email" and "Do not call" pills; nothing when the contact may be reached both ways. */
export function ContactFlags({ contact }: { readonly contact: Pick<ContactRow, 'emailOptOut' | 'doNotCall'> }): React.ReactElement | null {
  if (!contact.emailOptOut && !contact.doNotCall) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {contact.emailOptOut ? <Pill tone="danger">No email</Pill> : null}
      {contact.doNotCall ? <Pill tone="danger">Do not call</Pill> : null}
    </span>
  );
}

// ─── Engagement timeline ────────────────────────────────────────────────────

const TIMELINE_ICON: Readonly<Record<TimelineKind, LucideIcon>> = {
  [TIMELINE_KIND.email]: MailIcon,
  [TIMELINE_KIND.call]: PhoneIcon,
  [TIMELINE_KIND.todo]: ListTodoIcon,
  [TIMELINE_KIND.stage]: MilestoneIcon,
  [TIMELINE_KIND.meeting]: CalendarIcon,
};

const TIMELINE_LABEL: Readonly<Record<TimelineKind, string>> = {
  [TIMELINE_KIND.email]: 'Email',
  [TIMELINE_KIND.call]: 'Call',
  [TIMELINE_KIND.todo]: 'To-do',
  [TIMELINE_KIND.stage]: 'Stage change',
  [TIMELINE_KIND.meeting]: 'Meeting',
};

const TIMELINE_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** One touch, written for a timeline row. */
export type TimelineEntry = Pick<TimelineItem, 'id' | 'kind' | 'title' | 'at'> & Partial<Pick<TimelineItem, 'contactId' | 'contactName'>>;

/**
 * Emails, calls, meetings, to-dos and stage moves, newest first, each with
 * who it was with when known.
 */
export function EngagementTimeline({
  items,
  hideContact = false,
  emptyMessage,
}: {
  readonly items: readonly TimelineEntry[];
  /** On a contact's own page every touch is with them. */
  readonly hideContact?: boolean;
  readonly emptyMessage: string;
}): React.ReactElement {
  if (items.length === 0) {
    return <EmptyState icon={<HistoryIcon aria-hidden className="size-6" />} title="No activity yet" message={emptyMessage} />;
  }
  return (
    <ul>
      {items.map((item) => {
        const Icon = TIMELINE_ICON[item.kind];
        return (
          <li key={item.id} className="flex gap-3 border-t border-portal-line py-3 first:border-t-0 first:pt-0">
            <span className="mt-0.5 text-portal-muted">
              <Icon aria-hidden className="size-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-portal-small">{item.title}</span>
              <span className="block text-portal-micro text-portal-muted">
                {TIMELINE_LABEL[item.kind]} · {TIMELINE_DATE.format(new Date(item.at))}
                {!hideContact && item.contactName ? (
                  <>
                    {' · with '}
                    <ContactLink id={item.contactId ?? ''} name={item.contactName} />
                  </>
                ) : null}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Deal tables ────────────────────────────────────────────────────────────

const DEAL_SORT_FIELDS = {
  name: 'name',
  program: 'programName',
  stage: 'stageName',
  eventDate: 'eventDate',
  owner: 'ownerName',
  value: 'totalCost',
} as const;
const DEAL_SORT_KEYS = Object.values(DEAL_SORT_FIELDS);

/**
 * One group of an account's deals (open, booked, or closed without booking),
 * sortable in the URL under its own prefix so the page's tables sort apart.
 */
export function DealTable({
  deals,
  prefix,
  defaultSort,
  emptyTitle,
  emptyMessage,
  role,
}: {
  readonly deals: readonly DealRow[];
  readonly prefix: string;
  readonly defaultSort: { readonly key: (typeof DEAL_SORT_FIELDS)[keyof typeof DEAL_SORT_FIELDS]; readonly direction: 'asc' | 'desc' };
  readonly emptyTitle: string;
  readonly emptyMessage: string;
  /** The contact's role on each deal, by deal id (contact page). */
  readonly role?: ReadonlyMap<string, string>;
}): React.ReactElement {
  const { sort, onSort } = useTableSort(DEAL_SORT_KEYS, defaultSort, { prefix });
  if (deals.length === 0) {
    return (
      <Panel>
        <EmptyState title={emptyTitle} message={emptyMessage} />
      </Panel>
    );
  }
  return (
    <DataTable
      headers={[
        { label: 'Deal', sortKey: DEAL_SORT_FIELDS.name },
        ...(role ? ['Role'] : []),
        { label: 'Stage', sortKey: DEAL_SORT_FIELDS.stage },
        { label: 'Event date', sortKey: DEAL_SORT_FIELDS.eventDate },
        { label: 'Owner', sortKey: DEAL_SORT_FIELDS.owner },
        { label: 'Value', sortKey: DEAL_SORT_FIELDS.value },
      ]}
      rightAlignedHeaders={[role ? 5 : 4]}
      sort={sort}
      onSort={onSort}
      minWidth={760}
    >
      {sortRows(deals, sort).map((deal) => (
        <tr key={deal.id} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small">
            <DealLink dealId={deal.id} className="font-bold text-brand hover:underline">
              {deal.name || 'Untitled deal'}
            </DealLink>
            <span className="block text-portal-micro text-portal-muted">{deal.programName || 'No program yet'}</span>
          </td>
          {role ? <td className="p-3 text-portal-small text-portal-muted">{role.get(deal.id) || '—'}</td> : null}
          <td className="p-3 text-portal-small">{deal.stageName ? <Pill tone={eventStageTone(deal.stageName)}>{deal.stageName}</Pill> : '—'}</td>
          <td className="p-3 text-portal-small whitespace-nowrap">{formatDay(deal.eventDate, { withYear: true })}</td>
          <td className="p-3 text-portal-small text-portal-muted">{deal.ownerName || '—'}</td>
          <td className="p-3 text-right text-portal-small whitespace-nowrap">{money(deal.totalCost)}</td>
        </tr>
      ))}
    </DataTable>
  );
}

// ─── Contact table ──────────────────────────────────────────────────────────

const CONTACT_TABLE_SORT_FIELDS = {
  name: 'name',
  title: 'title',
  email: 'email',
  lastActivity: 'lastActivityDate',
} as const;
const CONTACT_TABLE_SORT_KEYS = Object.values(CONTACT_TABLE_SORT_FIELDS);

/** An account's contacts, with how to reach them and whether they may be. */
export function AccountContactsTable({ contacts, prefix }: { readonly contacts: readonly ContactRow[]; readonly prefix: string }): React.ReactElement {
  const { sort, onSort } = useTableSort(CONTACT_TABLE_SORT_KEYS, { key: CONTACT_TABLE_SORT_FIELDS.lastActivity, direction: 'desc' }, { prefix });
  if (contacts.length === 0) {
    return (
      <Panel>
        <EmptyState title="No contacts yet" message="People at this account show here once they are added in Salesforce." />
      </Panel>
    );
  }
  return (
    <DataTable
      headers={[
        { label: 'Name', sortKey: CONTACT_TABLE_SORT_FIELDS.name },
        { label: 'Email and phone', sortKey: CONTACT_TABLE_SORT_FIELDS.email },
        { label: 'Last activity', sortKey: CONTACT_TABLE_SORT_FIELDS.lastActivity },
        'Reach out',
      ]}
      sort={sort}
      onSort={onSort}
      minWidth={760}
    >
      {sortRows(contacts, sort).map((contact) => (
        <tr key={contact.id} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small">
            <ContactLink id={contact.id} name={contact.name} className="font-bold text-brand hover:underline" />
            <span className="block text-portal-micro text-portal-muted">{contact.title || '—'}</span>
          </td>
          <td className="p-3 text-portal-small">
            <span className="block">{contact.email || '—'}</span>
            <span className="block text-portal-micro text-portal-muted">{contact.phone || 'No phone'}</span>
          </td>
          <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{formatDay(contact.lastActivityDate, { withYear: true })}</td>
          <td className="p-3 text-portal-small">
            {contact.emailOptOut || contact.doNotCall ? <ContactFlags contact={contact} /> : <span className="text-portal-muted">—</span>}
          </td>
        </tr>
      ))}
    </DataTable>
  );
}
